class PWAService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this._updateCheckTimer = null;
    this._lastUpdateCheckErrorAt = 0;
  }

  async register() {
    if (!this.isSupported) {
      console.log('PWA: Service Worker not supported');
      return false;
    }

    try {
      // Check if we're in development mode
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      // In development, unregister existing service workers and skip registration
      if (isDev) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log('PWA: Unregistered service worker for development');
        }
        
        // Clear all caches
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
        console.log('PWA: Cleared all caches for development');
        
        // Skip service worker registration in development to avoid errors
        // Service worker file might not be accessible during React dev server hot reload
        console.log('PWA: Skipping service worker registration in development mode');
        
        // Still set up install prompt handling
        this.setupInstallPrompt();
        
        return true;
      }
      
      // Production: Register service worker
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none' // Don't cache the service worker itself
        });
        console.log('PWA: Service Worker registered successfully');
        
        // Check for updates periodically (avoid spamming update() – browsers already revalidate SW scripts).
        this.checkForUpdates();
        if (this._updateCheckTimer) {
          clearInterval(this._updateCheckTimer);
        }
        this._updateCheckTimer = setInterval(() => this.checkForUpdates(), 30 * 60 * 1000); // every 30 min
      } catch (swError) {
        // If service worker registration fails, log but don't break the app
        console.warn('PWA: Service worker registration failed (this is OK in some environments):', swError.message);
        this.registration = null;
      }
      
      // Only set up update listeners if registration was successful
      if (this.registration) {
        // Update available
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('PWA: New version available');
                this.showUpdateNotification();
              }
            });
          }
        });
      }
      
      // Set up install prompt handling (works without service worker)
      this.setupInstallPrompt();

      return true;
    } catch (error) {
      console.error('PWA: Service Worker registration failed', error);
      return false;
    }
  }

  setupInstallPrompt() {
    // Install prompt handling is centralized in PWAProvider.
    // Keeping an extra listener here causes duplicate preventDefault() calls and confusing console warnings.
    return;
  }

  async checkForUpdates() {
    if (this.registration) {
      try {
        await this.registration.update();
      } catch (error) {
        // Some hosts intermittently fail fetching /sw.js; avoid console spam.
        const now = Date.now();
        const shouldLog = process.env.NODE_ENV === 'production' && (now - this._lastUpdateCheckErrorAt) > 6 * 60 * 60 * 1000;
        if (shouldLog) {
          console.warn('PWA: Update check failed', error?.message || String(error));
          this._lastUpdateCheckErrorAt = now;
        }
      }
    }
  }

  async requestNotificationPermission() {
    if (!this.isSupported || Notification.permission === 'granted') {
      return true;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  showUpdateNotification() {
    if (window.confirm('New version available! Click OK to reload.')) {
      this.reloadApp();
    }
  }

  async reloadApp() {
    // Clear all caches before reload
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    console.log('PWA: Cleared caches, reloading...');
    window.location.reload(true); // Force reload from server
  }

  async subscribeToPushNotifications() {
    // Push notifications disabled for now - no valid VAPID key
    console.log('PWA: Push notifications disabled - no VAPID key configured');
    return false;
    
    // Original code commented out:
    /*
    if (!this.isSupported || !this.registration) {
      console.log('PWA: Push notifications not supported or no service worker registration');
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) return subscription;

      const vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa40HI8LQaM8cW5nQGJzHjBPBwBvBfBfBfBfBfBfBfBfBfBfBfBfBfBfBf';
      
      const newSubscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      return newSubscription;
    } catch (error) {
      // Only log error if it's not the expected "no active Service Worker" error
      if (!error.message?.includes('no active Service Worker')) {
        console.error('PWA: Push subscription failed', error);
      } else {
        console.log('PWA: Push notifications not available - service worker not active');
      }
      return false;
    }
    */
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  showInstallPrompt() {
    // Custom install prompt logic
    const deferredPrompt = window.deferredPrompt;
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('PWA: User accepted install prompt');
        } else {
          console.log('PWA: User dismissed install prompt');
        }
        // Clear the deferredPrompt so it can only be used once
        window.deferredPrompt = null;
      });
      
      return true;
    } else {
      console.log('PWA: Install prompt not available');
      return false;
    }
  }

  isOnline() {
    return navigator.onLine;
  }

  /**
   * True when the app is running as an installed PWA (standalone/fullscreen), not in a browser tab.
   */
  isStandalone() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator.standalone === true)
    );
  }

  addOnlineListener(callback) {
    window.addEventListener('online', callback);
  }

  addOfflineListener(callback) {
    window.addEventListener('offline', callback);
  }
}

const pwaService = new PWAService();
export default pwaService;
